// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormLabel,
  Chip,
  Select,
   Checkbox, 
  ListItemText, 
  OutlinedInput,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import dayjs from "dayjs";
import Icon from "../../../components/icon";
import { ToastContainer, toast } from 'react-toastify';
// ** Custom Components
import CustomTextField from "components/mui/text-field";
import CustomAutocomplete from "components/mui/autocomplete";

// ** Logic
import { GetEmployees, GetTimeLog, SaveTimeLog } from "../logic/PunchClockLogic";

const parseServerDate = (s) => {
  if (!s) return null;
  // normalize "YYYY-MM-DD hh:mm:ss" → "YYYY-MM-DDThh:mm:ss"
  const str = s.includes(" ") && !s.includes("T")
    ? s.replace(" ", "T")
    : s;
  // parse *exactly* as local time
  return dayjs(str);
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  MenuListProps: {
    sx: {
      '& .MuiMenuItem-root': {
        padding: '4px 8px', // Better spacing for checkboxes
      }
    }
  }
};

const AddEmployee = ({
  open,
  handleClose,
  fetchTimeLog,
  initialData = null    
}) => {
  const theme = useTheme();

  const [employees, setEmployees] = useState([]);
  const [selectedEmps, setSelectedEmps] = useState([]); 
  const [timeLogId, setTimeLogId] = useState(0);
  const [clockIn, setClockIn] = useState(null);
  const [clockOut, setClockOut] = useState(null);
  const [errors, setErrors] = useState({
    employee: false,
    clockIn: false,
    clockOut: false,
    timeRange: false,
  });

  const isEdit = Boolean(initialData && initialData.timeLogID);
  useEffect(() => {
    if (!open) return;
    (async () => {
      const resp = await GetEmployees(0);
      if (!resp?.isError && Array.isArray(resp.result)) {
        setEmployees(resp.result);
      }
    })();

    if (isEdit) {
      // For edit mode, still use single select
      setSelectedEmps([initialData.employeeID]);
      setTimeLogId(initialData.timeLogID);
      setClockIn(parseServerDate(initialData.checkin));
      setClockOut(parseServerDate(initialData.checkout));
    } else {
      // For create mode, use multi-select
      setSelectedEmps([]);
      setTimeLogId(0);
      setClockIn(null);
      setClockOut(null);
    }

    setErrors({
      employee: false,
      clockIn: false,
      clockOut: false,
      timeRange: false,
    });
  }, [open, initialData]);
  
  useEffect(() => {
    if (!selectedEmps.length || isEdit) return;

    
    setTimeLogId(0);
    setClockIn(null);
    setClockOut(null);
  }, [selectedEmps, isEdit]);

  const validateForm = () => {
    const newErrors = {
      employee: selectedEmps.length === 0, 
      clockIn: !clockIn,
      clockOut: !clockOut,
      timeRange:
        clockIn &&
        clockOut &&
        (clockOut.isBefore(clockIn) || !clockIn.isSame(clockOut, "day")),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

 const handleSave = async () => {
  if (!validateForm()) return;

  const payload = {
    timeLogID: isEdit ? timeLogId : 0,
    employeeID: selectedEmps, // This is now an array
    checkIn: dayjs(clockIn).format("YYYY-MM-DDTHH:mm:ss"),
    checkOut: dayjs(clockOut).format("YYYY-MM-DDTHH:mm:ss"),
  };

  const action = isEdit ? "update" : "save";
  
  try {
    const result = await SaveTimeLog(payload, action);
    
    // Handle the multi-employee response
    if (result && result.success !== undefined) {
      if (result.success) {
        // All employees saved successfully
        toast.success(`Time logs ${isEdit ? 'updated' : 'created'} successfully for ${result.count} employees!`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        handleClose();
        fetchTimeLog();
      } else {
        // Some employees failed
        toast.warning(`${result.totalCount - result.failedCount} of ${result.totalCount} time logs ${isEdit ? 'updated' : 'created'} successfully. ${result.failedCount} failed.`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
           theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        handleClose();
        fetchTimeLog();
      }
    } else {
      // Single employee case (handled by SaveTimeLog's existing toast)
      handleClose();
      fetchTimeLog();
    }
  } catch (error) {
    console.error("Error saving time logs:", error);
    toast.error(`Error ${isEdit ? 'updating' : 'creating'} time logs`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
    });
  }
};
  const handleEmployeeChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedEmps(
      // On autofill we get a stringified value.
      typeof value === 'string' ? value.split(',') : value,
    );
    setErrors((prev) => ({ ...prev, employee: false }));
  };

  const getEmployeeName = (employeeID) => {
    const employee = employees.find(emp => emp.employeeID === employeeID);
    return employee ? `${employee.firstName} ${employee.lastName}` : '';
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle
        sx={{ borderBottom: "1px solid #D9D9D9", pb: "20px !important" }}
      >
        <Typography variant="h4" fontWeight="bold">
          {isEdit ? "Edit Employee" : "Add Employees"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={5}>
           <Grid item xs={12} md={6.1}>
            <FormLabel
              required
              sx={{
                fontWeight: 400,
                fontSize: "13px",
                marginBottom: "4px",
                color: "text.primary",
                "& .MuiFormLabel-asterisk": {
                  color: theme.palette.error.main,
                },
              }}
            >
              Employees
            </FormLabel>

            <CustomAutocomplete
              multiple
              fullWidth
              disableCloseOnSelect
              options={[
                { employeeID: "all", firstName: "Select All", lastName: "" },
                ...[...employees].sort((a, b) =>
                  (a.firstName + " " + a.lastName)
                    .toLowerCase()
                    .localeCompare((b.firstName + " " + b.lastName).toLowerCase())
                ),
              ]}
              getOptionLabel={(option) =>
                option.employeeID === "all"
                  ? "Select All"
                  : `${option.firstName} ${option.lastName}`
              }
              value={selectedEmps
                .map((id) => employees.find((emp) => emp.employeeID === id))
                .filter(Boolean)}
              onChange={(event, newValue) => {
                const isSelectAll = newValue.some((opt) => opt.employeeID === "all");

                if (isSelectAll) {
                  if (selectedEmps.length === employees.length) {
                    setSelectedEmps([]);
                  } else {
                    setSelectedEmps(employees.map((emp) => emp.employeeID));
                  }
                } else {
                  const selectedIDs = newValue.map((item) => item.employeeID);
                  setSelectedEmps(selectedIDs);
                }

                setErrors((prev) => ({ ...prev, employee: false }));
              }}
              isOptionEqualToValue={(option, value) =>
                option?.employeeID === value?.employeeID
              }
              renderOption={(props, option, { selected }) => {
                const isSelectAll = option.employeeID === "all";
                const allSelected = selectedEmps.length === employees.length;

                return (
                  <li {...props}>
                    <Checkbox
                      size="small"
                      style={{ marginRight: 8 }}
                      checked={isSelectAll ? allSelected : selected}
                      indeterminate={
                        isSelectAll &&
                        selectedEmps.length > 0 &&
                        selectedEmps.length < employees.length
                      }
                    />
                    <ListItemText
                      primary={
                        isSelectAll
                          ? "Select All"
                          : `${option.firstName} ${option.lastName}`
                      }
                      primaryTypographyProps={{
                        sx: {
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                    />
                  </li>
                );
              }}
              renderTags={(value, getTagProps) => {
                const totalSelected = selectedEmps.length;
                const allSelected = totalSelected === employees.length;
                if (totalSelected === 0) return null;

                const label = allSelected
                  ? `All Employees (${employees.length})`
                  : `${value[0]?.firstName ?? ""} ${value[0]?.lastName ?? ""}${
                      totalSelected > 1 ? ` +${totalSelected - 1}` : ""
                    }`;

                return (
                  <Chip
                    label={label}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      backgroundColor: theme.palette.grey[100],
                      borderColor: theme.palette.grey[300],
                      "& .MuiChip-label": {
                        display: "block",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        maxWidth: "140px",
                      },
                    }}
                  />
                );
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  error={errors.employee}
                  helperText={
                    errors.employee ? "At least one employee is required" : ""
                  }
                  placeholder="Search or Select Employees"
                  sx={{
                    "& .MuiInputBase-root": {
                      minHeight: "40px",
                      height: "40px",
                    },
                  }}
                />
              )}
              disabled={isEdit}
            />
          </Grid>

          {/* Clock-In */}
          <Grid item xs={12} md={6.1}>
            <FormLabel
              required
              sx={{
                fontWeight: 400,
                fontSize: "13px",
                marginBottom: "4px",
                color: "text.primary",
                "& .MuiFormLabel-asterisk": {
                  color: theme.palette.error.main,
                },
              }}
            >
              Punch In
            </FormLabel>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                disabled={selectedEmps.length === 0}
                disableFuture
                maxDateTime={clockOut ?? dayjs().endOf("day")}
                value={clockIn}
                onChange={(val) => {
                  setClockIn(val);
                  setErrors((prev) => ({
                    ...prev,
                    clockIn: false,
                    timeRange: false,
                  }));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: errors.clockIn,
                    helperText: errors.clockIn
                      ? "Punch In is required"
                      : "",
                    FormHelperTextProps: {
                      sx: { fontSize: "13px", ml: 0 },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.clockIn
                            ? theme.palette.error.main
                            : undefined,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.clockIn
                            ? theme.palette.error.main
                            : undefined,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: errors.clockIn
                            ? theme.palette.error.main
                            : undefined,
                        },
                    },
                  }}
                }}
                desktopModeMediaQuery="@media (max-width: 0px)"
              />
            </LocalizationProvider>
          </Grid>

          {/* Clock-Out */}
          <Grid item xs={12} md={6.1}>
            <FormLabel
              required
              sx={{
                fontWeight: 400,
                fontSize: "13px",
                marginBottom: "4px",
                color: "text.primary",
                "& .MuiFormLabel-asterisk": {
                  color: theme.palette.error.main,
                },
              }}
            >
              Punch Out
            </FormLabel>

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                disabled={selectedEmps.length === 0 || !clockIn}
                disableFuture
                minDateTime={clockIn ?? dayjs().startOf("day")}
                maxDateTime={dayjs().endOf("day")}
                value={clockOut}
                onChange={(val) => {
                  setClockOut(val);
                  setErrors((prev) => ({
                    ...prev,
                    clockOut: false,
                    timeRange: false,
                  }));
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: errors.clockOut || errors.timeRange,
                    helperText: errors.clockOut
                      ? "Punch Out is required"
                      : errors.timeRange,
                    FormHelperTextProps: {
                      sx: { fontSize: "13px", ml: 0 },
                    },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            errors.clockOut || errors.timeRange
                              ? theme.palette.error.main
                              : undefined,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            errors.clockOut || errors.timeRange
                              ? theme.palette.error.main
                              : undefined,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor:
                            errors.clockOut || errors.timeRange
                              ? theme.palette.error.main
                              : undefined,
                        },
                      },
                    },
                  },
                }}
                desktopModeMediaQuery="@media (max-width: 0px)"
              />
            </LocalizationProvider>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "20px 24px !important",
          boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
        }}
      >
        <Button variant="contained" color="info" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="contained" color="main" onClick={handleSave}>
          {isEdit ? "Save Changes" : "Add Employees"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEmployee;